import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Phone, Mail, Headphones, X } from 'lucide-react';
import { cn } from './ui/utils';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { SUPPORT_TEL, SUPPORT_WHATSAPP, SUPPORT_PHONE_DISPLAY, SUPPORT_EMAIL } from '../../lib/support';

interface SupportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SupportModal({ open, onOpenChange }: SupportModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[2000]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed top-[50%] left-[50%] z-[2001] w-[calc(100%-2rem)] max-w-xs translate-x-[-50%] translate-y-[-50%]",
            "rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200"
          )}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="absolute top-3 right-3 rounded-full p-1 opacity-70 hover:opacity-100 transition-opacity">
            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* Header */}
          <div className="flex flex-col items-center gap-1 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#8bc43e]/10 flex items-center justify-center">
              <Headphones className="w-5 h-5 text-[#8bc43e]" />
            </div>
            <DialogTitle className="text-base font-semibold text-gray-900 dark:text-white">
              Contact Support
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              We're here to help!
            </DialogDescription>
          </div>

          {/* Contact options */}
          <div className="flex flex-col gap-2">
            {/* WhatsApp Button */}
            <a
              href={SUPPORT_WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800
                         hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                         border border-gray-200 dark:border-gray-700"
            >
              <div className="w-9 h-9 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">WhatsApp Us</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Chat with support</p>
              </div>
            </a>

            {/* Phone Button */}
            <a
              href={SUPPORT_TEL}
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800
                         hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                         border border-gray-200 dark:border-gray-700"
            >
              <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Phone className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Call Us</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{SUPPORT_PHONE_DISPLAY}</p>
              </div>
            </a>

            {/* Email Button */}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800
                         hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                         border border-gray-200 dark:border-gray-700"
            >
              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Email Us</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{SUPPORT_EMAIL}</p>
              </div>
            </a>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
